/**
 * Web app only: route every records call to the server-backed web backend and
 * tell the UI when records change underneath it. Called once at startup from
 * index.js; a no-op on desktop.
 */
import { setRecordsBackend } from '../recordsClient';
import { isWebApp } from '../../platform/target';
import { createWebRecordsBackend } from './webRecordsBackend';
import { useAuthStore } from '../../store/authStore';
import { addSessionEndedListener } from '../../services/channelService';
import { bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { bumpBoards } from '../../extensions/bundled/task-manager/boardsUiStore';
import { bumpCalendarEvents } from '../../extensions/bundled/calendar/calendarUiStore';
import { bumpGraph } from '../../graph/graphUiStore';

import { handleSyncRejected } from '../../services/syncRejections';

/** Extension id -> the UI refresh that re-reads it. */
const REFRESH = {
  documents: () => bumpDocumentsTree(),
  'task-manager': () => bumpBoards(),
  calendar: () => bumpCalendarEvents(),
  projects: () => bumpGraph(),
};

function refreshAll() {
  Object.values(REFRESH).forEach((fn) => fn());
}

let installed = null;

export function installWebRecords() {
  if (installed || !isWebApp()) return installed;
  const backend = createWebRecordsBackend({
    onRemoteChange: (_scope, extensionIds) => {
      extensionIds.forEach((id) => REFRESH[id]?.());
      // A new board or document is also a new node in the graph.
      if (!extensionIds.has('projects')) bumpGraph();
    },
    onRejected: (scopeKey, refused) =>
      handleSyncRejected({
        workspaceId: scopeKey,
        web: true,
        rejected: refused.map((r) => ({ ...r, entityType: 'extension_documents' })),
      }),
    // Everything the web app holds is a cloud item by definition — it has no
    // device storage to keep anything in — so every scope syncs (card C9).
    // What the Free plan limits is how MUCH may live in the cloud, which the
    // server enforces and the titlebar reports.
    canSync: () => true,
  });
  setRecordsBackend(backend);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) backend.refresh();
  });
  addSessionEndedListener(() => {
    backend.reset();
    refreshAll();
  });
  // Stores that read before sign-in finished saw the signed-out scope; the
  // signed-in one is a different bucket, so have them read again. A plan
  // change can turn sync on or off (Free needs consent, paid does not), so it
  // starts the scopes over too.
  const identity = (state) =>
    state.isAuthenticated && state.user?.id != null ? { id: state.user.id, plan: state.user.plan || 'free' } : null;
  let last = identity(useAuthStore.getState());
  useAuthStore.subscribe(async (state) => {
    const next = identity(state);
    if (next?.id === last?.id && next?.plan === last?.plan) return;
    // A plan change on the same account keeps its queued edits; a different
    // account (or none) must never push the previous account's queue.
    const sameAccount = next != null && last != null && next.id === last.id;
    last = next;
    await backend.reset({ flush: sameAccount });
    refreshAll();
  });

  installed = backend;
  return backend;
}
